'use client';

import { useState, type KeyboardEvent, type MutableRefObject, type ReactNode } from 'react';
import Link from 'next/link';
import {
  AcademicCapIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  Bars3Icon,
  TableCellsIcon,
  CalendarIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  LightBulbIcon,
  PlusIcon,
  SpeakerWaveIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

import AcademicCourseLabel from '@/app/Components/AcademicCourseLabel';
import AcademicProgramBadge from '@/app/Components/AcademicProgramBadge';
import { Button, IconButton, buttonClassName } from '@/app/Components/Button';
import { Field, Input, Select } from '@/app/Components/Field';
import Modal from '@/app/Components/Modal';
import SmsEnrollmentClient from '@/app/course-sniper/sms/SmsEnrollmentClient';
import {
  EntityCard,
  OperationalDataRow,
  OperationalEvidence,
  OperationalMetric,
  OperationalPage,
  OperationalResult,
  OperationalSection,
  OperationalState,
  OperationalStatus,
  Surface,
} from '@/app/Components/OperationalPage';
import { RUTGERS_GRADE_OPTIONS } from '@/app/lib/rutgersGpa';
import { rawColorUsage } from '@/app/design-system/colorUsage';
import type { AcademicPalettePreferences, AcademicPalettePreset } from '@/app/lib/academicPalette';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import type { CourseGradeEntry } from '@/app/services/userAcademicData';
import type { Degree, RutgersCourse } from '@/app/types/interfaces';

import type { ProfileTabId, ProfileViewModel } from './profileViewModel';

export type ProfileSchedule = {
  schedule_id?: string | number | null;
  name?: string | null;
  description?: string | null;
  is_public?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ProfileCourseList = {
  id?: string | number | null;
  name?: string | null;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ProfileDegreePlan = {
  id: string | number;
  name?: string | null;
  is_shareable?: boolean | null;
  created_at: string;
};

export type ProfileMemory = {
  id: string;
  memory_key: string;
  memory_value: string;
  created_at: string;
  updated_at: string;
};

export type ProfileAcademicData = {
  year: string;
  majors: string[];
  school: string;
  referral_source: string;
};

export type ProfileOverviewCard = { label: string; value: string; detail: string };
export type EditableSwatch = { label: string; solid: string };
export type InteractionFeedbackMode = 'off' | 'important' | 'enhanced';

type AccountPanelProps = {
  displayName: string;
  email: string;
  overviewCards: ProfileOverviewCard[];
  academicData: ProfileAcademicData;
  academicErrors: Record<string, string>;
  isEditingAcademic: boolean;
  academicSaving: boolean;
  degrees: Degree[];
  majorSearch: string;
  onMajorSearchChange: (value: string) => void;
  onAcademicDataChange: (value: ProfileAcademicData) => void;
  onToggleAcademicEdit: () => void;
  onCancelAcademicEdit: () => void;
  onSaveAcademic: () => void | Promise<void>;
  feedbackMode: InteractionFeedbackMode;
  onFeedbackModeChange: (mode: InteractionFeedbackMode) => void;
  palettePreferences: AcademicPalettePreferences;
  palettePresets: AcademicPalettePreset[];
  explorerPreferences: { defaultView: 'grid' | 'list' | 'table'; departmentColorsEnabled: boolean };
  onExplorerDefaultViewChange: (value: 'grid' | 'list' | 'table') => void;
  onExplorerDepartmentColorsChange: (value: boolean) => void;
  onCoursePaletteChange: (id: string) => void;
  onProgramPaletteChange: (id: string) => void;
  onDeleteCustomPalette: (id: string) => void;
  onResetPalettes: () => void;
  showCustomPaletteBuilder: boolean;
  onToggleCustomPaletteBuilder: () => void;
  customPaletteName: string;
  customPaletteDescription: string;
  customPaletteError: string | null;
  customCourseSwatches: EditableSwatch[];
  customProgramSwatches: EditableSwatch[];
  onCustomPaletteNameChange: (value: string) => void;
  onCustomPaletteDescriptionChange: (value: string) => void;
  onCustomSwatchChange: (kind: 'course' | 'program', index: number, field: 'label' | 'solid', value: string) => void;
  onSaveCustomPalette: () => void;
  onResetCustomPaletteBuilder: () => void;
  onDeleteAccount: () => void;
};

type PreviousClassesPanelProps = {
  completedCourses: RutgersCourse[];
  grades: CourseGradeEntry[];
  lastSaved: string | null;
  saving: boolean;
  saveError: string | null;
  onReloadRecord: () => void;
  addDialogOpen: boolean;
  searchQuery: string;
  searchResults: RutgersCourse[];
  onSearchQueryChange: (value: string) => void;
  onOpenAddDialog: () => void;
  onCloseAddDialog: () => void;
  onAddCourse: (course: RutgersCourse) => void | Promise<void>;
  onRemoveCourse: (course: RutgersCourse) => void;
  onGradeChange: (course: RutgersCourse, grade: string) => void | Promise<void>;
  onOpenPrerequisiteChecker: () => void;
};

type SchedulesPanelProps = {
  schedules: ProfileSchedule[];
  primaryScheduleId: string | null;
  onOpenScheduling: () => void;
  onOpenSchedule: (id: string) => void;
  onDeleteSchedule: (schedule: ProfileSchedule) => void;
};

type ConnectionsPanelProps = {
  acceptedCount: number | null;
  incomingRequestCount: number | null;
  sentRequestCount: number | null;
  publicScheduleCount: number;
};

type CourseListsPanelProps = {
  lists: ProfileCourseList[];
  onOpenCourseLists: () => void;
  onOpenList: (id: string) => void;
  onShareList: (list: ProfileCourseList) => void;
  onDeleteList: (list: ProfileCourseList) => void;
};

type DegreePlansPanelProps = {
  plans: ProfileDegreePlan[];
  retrieved: boolean;
  onRetry: () => void;
  onOpenPlanner: () => void;
  onOpenPlan: (id: string) => void;
  onDeletePlan: (plan: ProfileDegreePlan) => void;
};

type MemoriesPanelProps = {
  memories: ProfileMemory[];
  loading: boolean;
  onOpenAssistant: () => void;
  onDeleteMemory: (memory: ProfileMemory) => void;
};

export type BillingPanelProps = {
  state: 'idle' | 'loading' | 'ready' | 'error';
  accessStatus: 'free' | 'active';
  accessUntil: string | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  hasBillingAccount: boolean;
  checkoutState: 'processing' | 'cancelled' | null;
  actionBusy: 'checkout' | 'portal' | null;
  error: string | null;
  onStartCheckout: () => void;
  onOpenPortal: () => void;
  onRetry: () => void;
};

export type ProfileWorkspaceViewProps = {
  embedded?: boolean;
  summary: ProfileViewModel;
  activeTab: ProfileTabId;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  subscriptionLabel: string;
  completedCourseCount: number;
  memoryCount: number;
  onRetry: () => void;
  onSignOut: () => void;
  onTabChange: (tab: ProfileTabId) => void;
  onTabKeyDown: (event: KeyboardEvent<HTMLButtonElement>, index: number) => void;
  tabRefs: MutableRefObject<Array<HTMLButtonElement | null>>;
  account: AccountPanelProps;
  previousClasses: PreviousClassesPanelProps;
  connections: ConnectionsPanelProps;
  schedules: SchedulesPanelProps;
  courseLists: CourseListsPanelProps;
  degreePlans: DegreePlansPanelProps;
  billing: BillingPanelProps;
  professionalContent: ReactNode;
  memories: MemoriesPanelProps;
};

const tabDefinitions = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'previous_classes', label: 'Classes', icon: AcademicCapIcon },
  { id: 'connections', label: 'Connections', icon: UserGroupIcon },
  { id: 'schedules', label: 'Schedules', icon: CalendarIcon },
  { id: 'courses', label: 'Course Lists', icon: BookOpenIcon },
  { id: 'degree_plans', label: 'Degree Plans', icon: DocumentTextIcon },
  { id: 'billing', label: 'Billing', icon: SparklesIcon },
  { id: 'sms', label: 'SMS', icon: DevicePhoneMobileIcon },
  { id: 'professional', label: 'Professional', icon: DocumentTextIcon },
  { id: 'memories', label: 'AI Memories', icon: LightBulbIcon },
] as const satisfies ReadonlyArray<{ id: ProfileTabId; label: string; icon: typeof UserIcon }>;

function EmptyProfileState({ title, description, actionLabel, onAction }: { title: string; description: string; actionLabel: string; onAction: () => void }) {
  return <OperationalState kind="empty" title={title} description={description} action={<Button onClick={onAction}>{actionLabel}</Button>} />;
}

function AccountIdentity({ displayName, email, avatarUrl, subscriptionLabel, completedCourseCount, onSignOut }: Pick<ProfileWorkspaceViewProps, 'displayName' | 'email' | 'avatarUrl' | 'subscriptionLabel' | 'completedCourseCount' | 'onSignOut'>) {
  const initials = displayName.trim().slice(0, 1).toUpperCase() || 'S';
  return (
    <Surface aria-label="Account identity">
      <div className="flex flex-col items-center text-center">
        {avatarUrl ? (
          <img src={avatarUrl} alt={`${displayName} profile`} className="mb-2 h-14 w-14 rounded-full border-4 lg:mb-4 lg:h-24 lg:w-24 border-[var(--ss-brand)] object-cover" />
        ) : (
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full lg:mb-4 lg:h-24 lg:w-24 bg-[var(--ss-brand)] text-3xl font-bold text-[var(--ss-brand-contrast)]" aria-hidden="true">{initials}</div>
        )}
        <h2 className="text-lg font-semibold text-[var(--ss-text)] lg:text-xl">{displayName}</h2>
        <p className="mt-1 break-all text-sm text-[var(--ss-text-muted)]">{email}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2 lg:mt-4">
          <OperationalStatus>{subscriptionLabel}</OperationalStatus>
          <OperationalStatus tone="info">{completedCourseCount} classes tracked</OperationalStatus>
        </div>
        <Button className="mt-3 w-full lg:mt-5" intent="secondary" onClick={onSignOut}>Sign out</Button>
      </div>
    </Surface>
  );
}

function ProfileTabNavigation({ activeTab, completedCourseCount, memoryCount, connections, onTabChange, onTabKeyDown, tabRefs }: Pick<ProfileWorkspaceViewProps, 'activeTab' | 'completedCourseCount' | 'memoryCount' | 'connections' | 'onTabChange' | 'onTabKeyDown' | 'tabRefs'>) {
  const connectionRequestCount = connections.incomingRequestCount !== null && connections.incomingRequestCount > 0
    ? connections.incomingRequestCount
    : null;
  return (
    <Surface aria-label="Profile section navigation">
      <h2 className="sr-only text-base font-semibold text-[var(--ss-text)] sm:not-sr-only">Account sections</h2>
      <div className="flex gap-2 overflow-x-auto sm:mt-4 sm:grid sm:grid-cols-2 lg:grid-cols-1" role="tablist" aria-label="Profile sections">
        {tabDefinitions.map((item, index) => {
          const Icon = item.icon;
          const selected = activeTab === item.id;
          const count = item.id === 'previous_classes'
            ? completedCourseCount
            : item.id === 'connections'
              ? connectionRequestCount
              : item.id === 'memories'
                ? memoryCount
                : null;
          return (
            <button
              key={item.id}
              ref={(node) => { tabRefs.current[index] = node; }}
              id={`profile-tab-${item.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`profile-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              onClick={() => onTabChange(item.id)}
              className="flex min-h-12 w-auto shrink-0 items-center gap-2 rounded-control border border-transparent sm:w-full px-3 text-left text-sm font-medium text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] data-[selected=true]:border-[var(--ss-brand)] data-[selected=true]:bg-[var(--ss-selection)] data-[selected=true]:text-[var(--ss-text)]"
              data-selected={selected}
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{item.label}</span>
              {count !== null ? <OperationalStatus>{count}</OperationalStatus> : null}
            </button>
          );
        })}
      </div>
    </Surface>
  );
}

function AcademicInformation({ props }: { props: AccountPanelProps }) {
  const { academicData, academicErrors, degrees, isEditingAcademic, majorSearch } = props;
  const filteredDegrees = degrees.filter((degree) => degree.Name.toLowerCase().includes(majorSearch.toLowerCase()) && !academicData.majors.includes(degree.Name)).slice(0, 10);
  return (
    <OperationalSection
      title="Academic information"
      description="Used by prerequisite checks, degree planning, and academic recommendations."
      actions={<Button intent="secondary" onClick={props.onToggleAcademicEdit}>{isEditingAcademic ? 'Cancel editing' : 'Edit academic info'}</Button>}
    >
      {isEditingAcademic ? (
        <div className="space-y-4">
          <Field label="Academic year" error={academicErrors.year}>
            {(fieldProps) => (
              <Select {...fieldProps} value={academicData.year} onChange={(event) => props.onAcademicDataChange({ ...academicData, year: event.target.value })}>
                <option value="">Select your year</option>
                {['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate Student', 'Other'].map((year) => <option key={year} value={year}>{year}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Add a major" hint="Search by program name." error={academicErrors.majors}>
            {(fieldProps) => <Input {...fieldProps} value={majorSearch} onChange={(event) => props.onMajorSearchChange(event.target.value)} placeholder="Search programs" />}
          </Field>
          {majorSearch ? (
            <div className="max-h-52 overflow-y-auto rounded-card border border-[var(--ss-border)]" aria-label="Major search results">
              {filteredDegrees.length ? filteredDegrees.map((degree) => (
                <button key={degree.ID} type="button" className="flex min-h-12 w-full items-center border-b border-[var(--ss-border)] px-3 text-left last:border-b-0 hover:bg-[var(--ss-surface-subtle)]" onClick={() => { props.onAcademicDataChange({ ...academicData, majors: [...academicData.majors, degree.Name] }); props.onMajorSearchChange(''); }}>
                  <AcademicProgramBadge name={degree.Name} degreeCode={degree['Degree Code']} startTerm={degree['Start Term']} compact interactive={false} />
                </button>
              )) : <p className="p-3 text-sm text-[var(--ss-text-muted)]">No matching programs.</p>}
            </div>
          ) : null}
          {academicData.majors.length ? (
            <div className="flex flex-wrap gap-2" aria-label="Selected majors">
              {academicData.majors.map((major) => <div key={major} className="flex min-w-0 max-w-full items-center gap-1"><AcademicProgramBadge name={major} compact allowWrap className="max-w-[min(100%,18rem)]" interactive={false} /><IconButton label={`Remove ${major}`} intent="secondary" onClick={() => props.onAcademicDataChange({ ...academicData, majors: academicData.majors.filter((item) => item !== major) })}><XMarkIcon className="h-4 w-4" /></IconButton></div>)}
            </div>
          ) : null}
          <Field label="School" error={academicErrors.school}>
            {(fieldProps) => (
              <Select {...fieldProps} value={academicData.school} onChange={(event) => props.onAcademicDataChange({ ...academicData, school: event.target.value })}>
                <option value="">Select your school</option>
                {['School of Arts and Sciences', 'School of Engineering', 'School of Business', 'School of Environmental and Biological Sciences', 'School of Communication and Information', 'School of Social Work', 'School of Nursing', 'School of Health Professions', 'School of Pharmacy', 'School of Criminal Justice', 'School of Public Affairs and Administration', 'Graduate School', 'Other'].map((school) => <option key={school} value={school}>{school}</option>)}
              </Select>
            )}
          </Field>
          <Field label="How did you hear about us?">
            {(fieldProps) => (
              <Select {...fieldProps} value={academicData.referral_source} onChange={(event) => props.onAcademicDataChange({ ...academicData, referral_source: event.target.value })}>
                <option value="">Select an option</option>
                {['Friend', 'Social Media', 'Google Search', 'Reddit', 'University Website', 'Professor Recommendation', 'Other'].map((source) => <option key={source} value={source}>{source}</option>)}
              </Select>
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button loading={props.academicSaving} loadingLabel="Saving…" onClick={props.onSaveAcademic}>Save changes</Button>
            <Button intent="secondary" disabled={props.academicSaving} onClick={props.onCancelAcademicEdit}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div>
          <OperationalDataRow label="Academic year" value={academicData.year || 'Not set'} />
          <div className="operational-data-row grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)]"><div className="operational-data-label">Majors</div><div className="min-w-0">{academicData.majors.length ? <span className="flex max-w-full flex-wrap gap-2 sm:justify-end">{academicData.majors.map((major) => <AcademicProgramBadge key={major} name={major} compact allowWrap className="max-w-full sm:max-w-[min(100%,28rem)]" />)}</span> : 'Not set'}</div></div>
          <OperationalDataRow label="School" value={academicData.school || 'Not set'} />
          <OperationalDataRow label="Referral source" value={academicData.referral_source || 'Not set'} />
        </div>
      )}
    </OperationalSection>
  );
}

function FeedbackPreferences({ mode, onChange }: { mode: InteractionFeedbackMode; onChange: (mode: InteractionFeedbackMode) => void }) {
  const options = [
    { id: 'enhanced' as const, title: 'All actions', description: 'A quiet tick and brief haptic feedback for enabled buttons.' },
    { id: 'important' as const, title: 'Important actions', description: 'Feedback only for primary, submit, and marked actions.' },
    { id: 'off' as const, title: 'Off', description: 'No Scarlet Sync sound or haptic feedback.' },
  ];
  return (
    <OperationalSection title="Sound & haptics" description="Choose how Scarlet Sync acknowledges actions on this device." actions={<SpeakerWaveIcon className="h-5 w-5 text-[var(--ss-brand)]" aria-hidden="true" />}>
      <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Interaction feedback level">
        {options.map((option) => (
          <button key={option.id} type="button" role="radio" aria-checked={mode === option.id} data-selected={mode === option.id} onClick={() => onChange(option.id)} className="min-h-12 rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 text-left hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] data-[selected=true]:border-[var(--ss-brand)] data-[selected=true]:bg-[var(--ss-selection)]">
            <span className="block text-sm font-semibold text-[var(--ss-text)]">{option.title}</span>
            <span className="mt-1 block text-xs leading-5 text-[var(--ss-text-muted)]">{option.description}</span>
          </button>
        ))}
      </div>
    </OperationalSection>
  );
}

function PaletteSwatches({ preset, kind }: { preset: AcademicPalettePreset; kind: 'course' | 'program' }) {
  const swatches = kind === 'course' ? preset.courseSwatches : preset.programSwatches;
  return <div className="mt-3 flex flex-wrap gap-2" aria-label={`${preset.name} ${kind} colors`}>{swatches.slice(0, 6).map((swatch) => { const usage = rawColorUsage({ purpose: 'other:academic-identity', slot: 'fill', value: swatch.solid }); return <span key={swatch.id} {...usage} className="inline-flex items-center gap-2 text-xs text-[var(--ss-text-muted)]"><span className="h-4 w-4 rounded-full border border-[var(--ss-border)] bg-[var(--ss-raw-color-fill)]" aria-hidden="true" />{swatch.label}</span>; })}</div>;
}

function PaletteChoice({ preset, kind, selected, onSelect, onDelete }: { preset: AcademicPalettePreset; kind: 'course' | 'program'; selected: boolean; onSelect: () => void; onDelete: () => void }) {
  return (
    <OperationalResult className={selected ? 'border-[var(--ss-brand)] bg-[var(--ss-selection)]' : ''} actions={preset.isCustom ? <Button size="sm" intent="secondary" onClick={onDelete}>Delete</Button> : undefined}>
      <button type="button" onClick={onSelect} className="min-h-12 w-full text-left" aria-pressed={selected}>
        <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ss-text)]">{preset.name}{preset.isCustom ? <OperationalStatus>Custom</OperationalStatus> : null}{selected ? <CheckCircleIcon className="h-5 w-5 text-[var(--ss-brand)]" aria-label="Selected" /> : null}</span>
        <span className="mt-1 block text-xs text-[var(--ss-text-muted)]">{preset.description}</span>
        <PaletteSwatches preset={preset} kind={kind} />
      </button>
    </OperationalResult>
  );
}

function CustomPaletteBuilder({ props }: { props: AccountPanelProps }) {
  return (
    <Surface variant="inset" aria-label="Custom palette builder">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Palette name" error={props.customPaletteError}>{(fieldProps) => <Input {...fieldProps} value={props.customPaletteName} onChange={(event) => props.onCustomPaletteNameChange(event.target.value)} placeholder="Midnight Notes" />}</Field>
        <Field label="Description">{(fieldProps) => <Input {...fieldProps} value={props.customPaletteDescription} onChange={(event) => props.onCustomPaletteDescriptionChange(event.target.value)} placeholder="Low-glare tones for long planning sessions" />}</Field>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {([{ key: 'course' as const, title: 'Course swatches', items: props.customCourseSwatches }, { key: 'program' as const, title: 'Program swatches', items: props.customProgramSwatches }]).map((group) => (
          <div key={group.key}>
            <h3 className="text-sm font-semibold text-[var(--ss-text)]">{group.title}</h3>
            <div className="mt-3 space-y-3">
              {group.items.map((swatch, index) => (
                <div key={`${group.key}-${index}`} className="grid grid-cols-[minmax(0,1fr)_112px_48px] gap-2">
                  <Input aria-label={`${group.title} ${index + 1} label`} value={swatch.label} onChange={(event) => props.onCustomSwatchChange(group.key, index, 'label', event.target.value)} />
                  <Input aria-label={`${group.title} ${index + 1} hex color`} value={swatch.solid} onChange={(event) => props.onCustomSwatchChange(group.key, index, 'solid', event.target.value)} className="font-mono" />
                  <Input aria-label={`${group.title} ${index + 1} color picker`} type="color" value={swatch.solid} onChange={(event) => props.onCustomSwatchChange(group.key, index, 'solid', event.target.value)} className="min-h-12 p-1" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-3"><Button onClick={props.onSaveCustomPalette}>Save custom palette</Button><Button intent="secondary" onClick={props.onResetCustomPaletteBuilder}>Reset builder</Button></div>
    </Surface>
  );
}

function PalettePreferences({ props }: { props: AccountPanelProps }) {
  const selectedCourse = props.palettePresets.find((preset) => preset.id === props.palettePreferences.coursePaletteId) ?? props.palettePresets[0];
  const selectedProgram = props.palettePresets.find((preset) => preset.id === props.palettePreferences.programPaletteId) ?? props.palettePresets[0];
  return (
    <OperationalSection title="Academic color palettes" description="Course and program identity colors are stored locally on this device." actions={<Button intent="secondary" onClick={props.onResetPalettes}>Reset</Button>}>
      <Surface variant="inset" aria-labelledby="explorer-appearance-heading">
        <h3 id="explorer-appearance-heading" className="text-sm font-semibold text-[var(--ss-text)]">Explorer appearance</h3>
        <p className="mt-1 text-xs text-[var(--ss-text-muted)]">Choose how Course Explorer opens. Department accents use the active course palette.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <fieldset><legend className="text-xs font-semibold text-[var(--ss-text-muted)]">Default results view</legend><div className="mt-2 flex flex-wrap gap-2">{(['grid', 'list', 'table'] as const).map((view) => <Button key={view} intent={props.explorerPreferences.defaultView === view ? 'primary' : 'secondary'} aria-pressed={props.explorerPreferences.defaultView === view} onClick={() => props.onExplorerDefaultViewChange(view)}>{view === 'grid' ? 'Grid' : view === 'list' ? 'List' : 'Table'}</Button>)}</div></fieldset>
          <div className="flex items-center justify-between gap-4 rounded-card border border-[var(--ss-border)] p-3"><div><p className="text-sm font-medium text-[var(--ss-text)]">Department colors</p><p className="text-xs text-[var(--ss-text-muted)]">Show a stripe and labeled department badge.</p></div><Button intent={props.explorerPreferences.departmentColorsEnabled ? 'primary' : 'secondary'} role="switch" aria-checked={props.explorerPreferences.departmentColorsEnabled} aria-label="Show department colors in Course Explorer" onClick={() => props.onExplorerDepartmentColorsChange(!props.explorerPreferences.departmentColorsEnabled)}>{props.explorerPreferences.departmentColorsEnabled ? 'On' : 'Off'}</Button></div>
        </div>
      </Surface>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3"><div><p className="text-sm font-medium text-[var(--ss-text)]">Custom palettes</p><p className="text-xs text-[var(--ss-text-muted)]">Create device-local course and program palettes.</p></div><Button intent="secondary" onClick={props.onToggleCustomPaletteBuilder}>{props.showCustomPaletteBuilder ? 'Close builder' : 'Create custom palette'}</Button></div>
      {props.showCustomPaletteBuilder ? <div className="mt-4"><CustomPaletteBuilder props={props} /></div> : null}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div><h3 className="text-sm font-semibold text-[var(--ss-text)]">Course palette</h3><p className="text-xs text-[var(--ss-text-muted)]">Active: {selectedCourse?.name ?? 'Unavailable'}</p><div className="mt-3 space-y-3">{props.palettePresets.map((preset) => <PaletteChoice key={`course-${preset.id}`} preset={preset} kind="course" selected={preset.id === props.palettePreferences.coursePaletteId} onSelect={() => props.onCoursePaletteChange(preset.id)} onDelete={() => props.onDeleteCustomPalette(preset.id)} />)}</div></div>
        <div><h3 className="text-sm font-semibold text-[var(--ss-text)]">Program palette</h3><p className="text-xs text-[var(--ss-text-muted)]">Active: {selectedProgram?.name ?? 'Unavailable'}</p><div className="mt-3 space-y-3">{props.palettePresets.map((preset) => <PaletteChoice key={`program-${preset.id}`} preset={preset} kind="program" selected={preset.id === props.palettePreferences.programPaletteId} onSelect={() => props.onProgramPaletteChange(preset.id)} onDelete={() => props.onDeleteCustomPalette(preset.id)} />)}</div></div>
      </div>
      <OperationalEvidence className="mt-6"><span className="font-semibold">Preview:</span> <span className="ml-2 inline-flex flex-wrap gap-2"><AcademicCourseLabel courseName="Data Structures" baseId="01198112" credits="4" compact /><AcademicProgramBadge name="Computer Science Major" degreeCode="198" compact /></span></OperationalEvidence>
    </OperationalSection>
  );
}

export function ProfileAccountPanel(props: AccountPanelProps) {
  return (
    <div className="space-y-6">
      <OperationalSection title="Profile information"><div aria-label="Personal information" className="grid gap-4 md:grid-cols-2"><Field label="Display name">{(fieldProps) => <Input {...fieldProps} readOnly value={props.displayName} />}</Field><Field label="Email">{(fieldProps) => <Input {...fieldProps} readOnly type="email" value={props.email} />}</Field></div></OperationalSection>
      <AcademicInformation props={props} />
      <details className="rounded-card border border-line bg-surface p-4"><summary className="min-h-12 cursor-pointer py-3 font-semibold text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Sound &amp; haptics</summary><FeedbackPreferences mode={props.feedbackMode} onChange={props.onFeedbackModeChange} /></details>
      <details className="rounded-card border border-line bg-surface p-4"><summary className="min-h-12 cursor-pointer py-3 font-semibold text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Appearance</summary><PalettePreferences props={props} /></details>
      <details className="rounded-card border border-line p-4"><summary className="min-h-12 cursor-pointer py-3 font-semibold text-critical focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Delete account</summary><OperationalSection title="Delete account" description="Permanently removes schedules, course lists, degree plans, and profile information."><OperationalEvidence tone="critical">This action cannot be undone.</OperationalEvidence><Button className="mt-4" intent="danger" onClick={props.onDeleteAccount}>Delete account</Button></OperationalSection></details>
    </div>
  );
}

export function PreviousClassesPanel(props: PreviousClassesPanelProps) {
  const savedGradeCount = props.grades.filter((entry) => entry.grade).length;
  const [courseView, setCourseView] = useState<'grid' | 'table'>('grid');
  const completed = [...new Map(props.completedCourses.map(course => [course.baseId.replace(/\D/g, ''), course])).values()];
  const knownCredits = completed.reduce((total, course) => total + (Number.isFinite(Number(course.credits)) ? Number(course.credits) : 0), 0);
  const unknownCredits = completed.some(course => !Number.isFinite(Number(course.credits)));
  const [recordSort, setRecordSort] = useState<'course' | 'credits'>('course');
  return (
    <div className="space-y-6">
      <OperationalSection title="Previous classes" actions={<div className="flex w-full flex-wrap items-center justify-center gap-2 sm:w-auto"><Button onClick={props.onOpenAddDialog}><PlusIcon className="h-5 w-5" /> Add course</Button><Button intent="secondary" onClick={props.onOpenPrerequisiteChecker}><ArrowTopRightOnSquareIcon className="h-5 w-5" /> Prerequisite checker</Button></div>}>
        <div className="flex flex-wrap gap-2"><OperationalStatus tone="positive">{props.completedCourses.length} completed</OperationalStatus><OperationalStatus>{knownCredits}{unknownCredits ? "+ known" : ""} credits</OperationalStatus><OperationalStatus>{savedGradeCount} grades {props.saveError ? 'shown' : 'saved'}</OperationalStatus>{props.saveError ? <OperationalStatus tone="warning">Unsaved changes</OperationalStatus> : props.saving ? <OperationalStatus tone="warning">Saving</OperationalStatus> : props.lastSaved ? <OperationalStatus>Saved {new Date(props.lastSaved).toLocaleString()}</OperationalStatus> : null}</div>
        {props.saveError ? <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 rounded-control border border-[var(--ss-warning)] bg-[var(--ss-warning-bg)] p-3 text-sm text-[var(--ss-text)]"><span>{props.saveError} Reloading replaces the edits shown here.</span><Button intent="secondary" onClick={props.onReloadRecord}>Reload academic record</Button></div> : null}
      </OperationalSection>
      <OperationalSection title={`Completed courses (${props.completedCourses.length})`} className="!rounded-none !border-0 !bg-transparent !px-0 !shadow-none" actions={<div className="inline-grid grid-cols-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-1" role="group" aria-label="Completed course view"><button type="button" aria-pressed={courseView === 'grid'} onClick={() => setCourseView('grid')} className={`inline-flex min-h-11 items-center gap-2 rounded-control px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${courseView === 'grid' ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-sm' : 'text-[var(--ss-text-muted)]'}`}><Squares2X2Icon className="h-4 w-4" />Grid</button><button type="button" aria-pressed={courseView === 'table'} onClick={() => setCourseView('table')} className={`inline-flex min-h-11 items-center gap-2 rounded-control px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${courseView === 'table' ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-sm' : 'text-[var(--ss-text-muted)]'}`}><TableCellsIcon className="h-4 w-4" />Table</button></div>}>
        {props.completedCourses.length === 0 ? <EmptyProfileState title="No completed courses yet" description="Add prior coursework so Scarlet Sync can reason about what you have finished." actionLabel="Add your first course" onAction={props.onOpenAddDialog} /> : courseView === 'table' ? <div className="overflow-x-auto" role="region" aria-label="Completed course table" tabIndex={0}><table className="w-full text-left text-sm"><thead className="border-b border-line text-xs text-content-muted"><tr><th scope="col"><button type="button" className="min-h-12" onClick={() => setRecordSort('course')}>Course</button></th><th scope="col"><button type="button" className="min-h-12 px-3" onClick={() => setRecordSort('credits')}>Credits</button></th><th scope="col">Grade</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead><tbody>{[...props.completedCourses].sort((a,b) => recordSort === 'course' ? a.courseName.localeCompare(b.courseName) : Number(b.credits) - Number(a.credits)).map(course => <tr key={course.id} className="border-b border-line last:border-b-0"><th scope="row" className="min-w-48 py-3 pr-3 font-medium"><span className="block">{course.fullTitle || course.courseName}</span><span className="mt-1 block font-mono text-xs font-normal text-content-muted">{formatBaseIdDisplay(course.baseId)}</span></th><td className="px-3 tabular-nums">{course.credits}</td><td className="min-w-32"><select aria-label={`Grade for ${course.courseName}`} className="ss-field min-h-12 w-full" value={props.grades.find(entry => entry.code.replace(/\D/g, '') === course.baseId.replace(/\D/g, ''))?.grade ?? ''} onChange={event => props.onGradeChange(course, event.target.value)}><option value="">Select</option>{RUTGERS_GRADE_OPTIONS.map(grade => <option key={grade} value={grade}>{grade}</option>)}</select></td><td className="pl-2"><IconButton label={`Remove ${course.courseName}`} intent="secondary" onClick={() => props.onRemoveCourse(course)}><XMarkIcon className="h-5 w-5" /></IconButton></td></tr>)}</tbody></table></div> : <div className={`grid gap-4 ${courseView === 'grid' ? 'md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>{props.completedCourses.map((course) => {
          const grade = props.grades.find((entry) => entry.code.replace(/[^0-9]/g, '') === course.baseId)?.grade ?? '';
          return (
            <EntityCard
              key={course.id}
              className={`min-w-0 overflow-hidden `}
              title={(
                <div className="min-w-0 max-w-full pr-2">
                  <AcademicCourseLabel
                    courseName={course.courseName}
                    baseId={course.baseId}
                    department={course.department}
                    classNumber={course.class_number}
                    credits={course.credits}
                    preventParentClick
                    showCode={false}
                    wrapTitle
                    className="!max-w-full !text-[var(--ss-text)]"
                  />
                </div>
              )}
              metadata={`${formatBaseIdDisplay(course.baseId)} ${course.credits} Credits`}
              status={<IconButton label={`Remove ${course.courseName}`} intent="secondary" onClick={() => props.onRemoveCourse(course)}><XMarkIcon className="h-5 w-5" /></IconButton>}
            >
              <Field label="Grade">
                {(fieldProps) => (
                  <Select {...fieldProps} value={grade} onChange={(event) => props.onGradeChange(course, event.target.value)}>
                    <option value="">Select</option>
                    {RUTGERS_GRADE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option === 'P' ? 'P (Pass)' : option}</option>
                    ))}
                  </Select>
                )}
              </Field>
            </EntityCard>
          );
        })}</div>}
      </OperationalSection>
      <Modal isOpen={props.addDialogOpen} onClose={props.onCloseAddDialog} title="Add completed course" size="lg">
        <Field label="Search courses" hint="Search by name, department:number, or title.">{(fieldProps) => <Input {...fieldProps} autoFocus value={props.searchQuery} onChange={(event) => props.onSearchQueryChange(event.target.value)} placeholder="640:250 or Linear Optimization" />}</Field>
        <div className="mt-4 max-h-96 space-y-2 overflow-y-auto">{props.searchResults.slice(0, 50).map((course) => <button key={course.id} type="button" className="min-h-12 w-full rounded-card border border-[var(--ss-border)] p-3 text-left hover:bg-[var(--ss-surface-subtle)]" onClick={() => props.onAddCourse(course)}><AcademicCourseLabel courseName={course.courseName} baseId={course.baseId} department={course.department} classNumber={course.class_number} credits={course.credits} preventParentClick /><span className="mt-1 block text-xs text-[var(--ss-text-muted)]">{course.department}:{course.class_number} · {course.credits} credits · {formatBaseIdDisplay(course.baseId)}</span></button>)}</div>
        {props.searchQuery && props.searchResults.length === 0 ? <div className="mt-4"><OperationalState kind="empty" title="No courses found" description={`No course matches “${props.searchQuery}”.`} /></div> : null}
      </Modal>
    </div>
  );
}

export function SchedulesPanel({ schedules, primaryScheduleId, onOpenScheduling, onOpenSchedule, onDeleteSchedule }: SchedulesPanelProps) {
  return (
    <OperationalSection title="Saved schedules" description="Control sharing and the schedule used by planning defaults." actions={<Button onClick={onOpenScheduling}>Open scheduling</Button>}>
      <OperationalEvidence><strong>Public</strong> schedules are visible to accepted friends in <Link href="/friends" className="font-semibold underline">Friends</Link>. <strong>Primary</strong> identifies the planning and Class Chat default.</OperationalEvidence>
      {schedules.length === 0 ? <div className="mt-4"><EmptyProfileState title="No saved schedules yet" description="Build a schedule, then return to reopen or manage saved versions." actionLabel="Open scheduling" onAction={onOpenScheduling} /></div> : <div className="mt-4 space-y-3">{[...schedules].sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()).map((schedule, index) => { const id = String(schedule.schedule_id ?? ''); const primary = id === primaryScheduleId; return <OperationalResult key={id || `schedule-${index}`} actions={<div className="flex flex-wrap gap-2"><Button size="sm" intent="secondary" disabled={!id} onClick={() => onOpenSchedule(id)}>Open</Button><Button size="sm" intent="danger" disabled={!id} onClick={() => onDeleteSchedule(schedule)}>Delete</Button></div>}><div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-[var(--ss-text)]">{schedule.name || 'Untitled schedule'}</h3>{primary ? <OperationalStatus tone="warning" icon={<StarIcon className="h-4 w-4" />}>Primary</OperationalStatus> : null}<OperationalStatus tone={schedule.is_public ? 'positive' : 'neutral'}>{schedule.is_public ? 'Public' : 'Private'}</OperationalStatus></div><p className="mt-1 text-sm text-[var(--ss-text-muted)]">{schedule.description || 'No description'}</p><p className="mt-1 text-xs text-[var(--ss-text-muted)]">Updated {new Date(schedule.updated_at || schedule.created_at || 0).toLocaleDateString()}</p></OperationalResult>; })}</div>}
    </OperationalSection>
  );
}

export function ConnectionsPanel({ acceptedCount, incomingRequestCount, sentRequestCount, publicScheduleCount }: ConnectionsPanelProps) {
  const available = acceptedCount !== null && incomingRequestCount !== null && sentRequestCount !== null;
  return (
    <OperationalSection
      title="Connections"
      description="Review who can see your Public schedules and continue to the full connection manager for requests and shared schedules."
      actions={<Link href="/friends" className={buttonClassName({ variant: 'primary' })}><UserGroupIcon className="h-5 w-5" aria-hidden="true" />Open Friends</Link>}
    >
      {!available ? (
        <OperationalState
          kind="stale"
          title="Connection summary unavailable"
          description="The friendship service did not return a current summary. Open Friends to retry requests, connections, and shared-schedule access."
          action={<Link href="/friends" className={buttonClassName({ variant: 'secondary' })}>Open connection manager</Link>}
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OperationalMetric label="Accepted" value={String(acceptedCount)} detail="People connected to you" />
            <OperationalMetric label="Requests" value={String(incomingRequestCount)} detail={incomingRequestCount === 1 ? 'Request waiting for your response' : 'Requests waiting for your response'} />
            <OperationalMetric label="Sent" value={String(sentRequestCount)} detail="Outgoing requests still pending" />
            <OperationalMetric label="Public schedules" value={String(publicScheduleCount)} detail="Your schedules visible to accepted friends" />
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <OperationalResult>
              <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-[var(--ss-text)]">Friend requests</h3><OperationalStatus tone={incomingRequestCount > 0 ? 'warning' : 'neutral'}>{incomingRequestCount > 0 ? `${incomingRequestCount} to review` : 'None waiting'}</OperationalStatus></div>
              <p className="mt-2 text-sm text-[var(--ss-text-muted)]">Accept or decline requests in Friends. Profile does not duplicate those account mutations.</p>
            </OperationalResult>
            <OperationalResult>
              <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-[var(--ss-text)]">Find people</h3><OperationalStatus tone="info">Search available</OperationalStatus></div>
              <p className="mt-2 text-sm text-[var(--ss-text-muted)]">Search by name or email and review suggested connections in the full manager.</p>
            </OperationalResult>
            <OperationalResult>
              <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-[var(--ss-text)]">Shared schedules</h3><OperationalStatus tone="info">Public only</OperationalStatus></div>
              <p className="mt-2 text-sm text-[var(--ss-text-muted)]">Choose an accepted friend to inspect schedules they explicitly marked Public.</p>
            </OperationalResult>
          </div>
          <OperationalEvidence className="mt-5"><strong>Sharing boundary:</strong> accepted connections can see only schedules marked <strong>Public</strong>. Private schedules remain private.</OperationalEvidence>
        </>
      )}
    </OperationalSection>
  );
}

export function CourseListsPanel({ lists, onOpenCourseLists, onOpenList, onShareList, onDeleteList }: CourseListsPanelProps) {
  return (
    <OperationalSection title="Course lists" description="Reusable course collections for planning and comparison." actions={<Button onClick={onOpenCourseLists}>Open course lists</Button>}>
      {lists.length === 0 ? <EmptyProfileState title="No course lists yet" description="Create lists for majors, electives, or backup options." actionLabel="Open course lists" onAction={onOpenCourseLists} /> : <div className="space-y-3">{[...lists].sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()).map((list, index) => { const id = String(list.id ?? ''); return <OperationalResult key={id || `list-${index}`} actions={<div className="flex flex-wrap gap-2"><Button size="sm" intent="secondary" disabled={!id} onClick={() => onOpenList(id)}>Open</Button><Button size="sm" intent="secondary" disabled={!id} onClick={() => onShareList(list)}>Copy link</Button><Button size="sm" intent="danger" disabled={!id} onClick={() => onDeleteList(list)}>Delete</Button></div>}><h3 className="font-semibold text-[var(--ss-text)]">{list.name || 'Untitled course list'}</h3><p className="mt-1 text-sm text-[var(--ss-text-muted)]">{list.description || 'No description'}</p></OperationalResult>; })}</div>}
    </OperationalSection>
  );
}

export function DegreePlansPanel({ plans, retrieved, onRetry, onOpenPlanner, onOpenPlan, onDeletePlan }: DegreePlansPanelProps) {
  return (
    <OperationalSection title="Degree plans" description="Roadmaps that connect requirements, credits, and semesters." actions={<Button onClick={onOpenPlanner}>Open degree planner</Button>}>
      {!retrieved ? <OperationalState kind="stale" title="Degree plans are unavailable" description="The plan service did not return a current list. Last known values are not treated as an empty collection." action={<Button intent="secondary" onClick={onRetry}>Retry degree plans</Button>} /> : plans.length === 0 ? <EmptyProfileState title="No degree plans yet" description="Create a roadmap to keep requirements and semesters visible." actionLabel="Open degree planner" onAction={onOpenPlanner} /> : <div className="space-y-3">{[...plans].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((plan) => <OperationalResult key={String(plan.id)} actions={<div className="flex flex-wrap gap-2"><Button size="sm" intent="secondary" onClick={() => onOpenPlan(String(plan.id))}>Open</Button><Button size="sm" intent="danger" onClick={() => onDeletePlan(plan)}>Delete</Button></div>}><div className="flex items-center gap-2"><h3 className="font-semibold text-[var(--ss-text)]">{plan.name || 'Untitled plan'}</h3>{plan.is_shareable ? <OperationalStatus tone="positive">Public</OperationalStatus> : null}</div><p className="mt-1 text-sm text-[var(--ss-text-muted)]">Created {new Date(plan.created_at).toLocaleDateString()}</p></OperationalResult>)}</div>}
    </OperationalSection>
  );
}

export function ProfessionalPanel({ children }: { children: ReactNode }) {
  return <OperationalSection title="Professional information" description="Details used to personalize research and outreach tools.">{children}</OperationalSection>;
}

export function MemoriesPanel({ memories, loading, onOpenAssistant, onDeleteMemory }: MemoriesPanelProps) {
  return (
    <OperationalSection title="AI memories" description="Review context saved by Ask Scarlet Sync." actions={<Button onClick={onOpenAssistant}>Open Ask Scarlet Sync</Button>}>
      {loading ? <OperationalState kind="loading" title="Loading memories" description="Retrieving saved context." /> : memories.length === 0 ? <EmptyProfileState title="No memories yet" description="Useful preferences and context saved by Ask Scarlet Sync will appear here." actionLabel="Open Ask Scarlet Sync" onAction={onOpenAssistant} /> : <div className="space-y-3">{memories.map((memory) => <OperationalResult key={memory.id} actions={<IconButton label={`Delete ${memory.memory_key}`} intent="danger" onClick={() => onDeleteMemory(memory)}><XMarkIcon className="h-5 w-5" /></IconButton>}><h3 className="font-semibold text-[var(--ss-text)]">{memory.memory_key}</h3><p className="mt-2 whitespace-pre-wrap text-sm text-[var(--ss-text-muted)]">{memory.memory_value}</p><p className="mt-2 text-xs text-[var(--ss-text-muted)]">Created {new Date(memory.created_at).toLocaleString()}{memory.updated_at !== memory.created_at ? ` · Updated ${new Date(memory.updated_at).toLocaleString()}` : ''}</p></OperationalResult>)}</div>}
    </OperationalSection>
  );
}

function BillingPanel({ state, accessStatus, accessUntil, subscriptionStatus, cancelAtPeriodEnd, currentPeriodEnd, hasBillingAccount, checkoutState, actionBusy, error, onStartCheckout, onOpenPortal, onRetry }: BillingPanelProps) {
  const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const accessDate = formatDate(accessUntil);
  const renewalDate = formatDate(currentPeriodEnd);
  const terminalSubscription = subscriptionStatus === 'canceled' || subscriptionStatus === 'incomplete_expired';
  const canSubscribe = accessStatus === 'free' && !['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(subscriptionStatus || '');
  if (state === 'loading' || state === 'idle') return <OperationalState kind="loading" title="Loading billing" description="Checking your Scarlet Sync subscription." />;
  if (state === 'error') return <OperationalState kind="error" title="Billing is unavailable" description={error || 'Your billing status could not be loaded.'} action={<Button intent="secondary" onClick={onRetry}>Retry</Button>} />;
  return (
    <OperationalSection title="Billing" description="$5 USD every six months from signup. Renews automatically until you cancel in Stripe.">
      {checkoutState === 'processing' && accessStatus !== 'active' ? <OperationalState kind="stale" title="Payment confirmation pending" description="We will update this page after Stripe confirms the paid invoice." action={<Button intent="secondary" onClick={onRetry}>Refresh status</Button>} /> : null}
      {checkoutState === 'cancelled' ? <OperationalState kind="empty" title="Checkout closed" description="Your billing status above is the source of truth for any subscription or paid access." /> : null}
      {accessStatus === 'active' ? (
        <OperationalResult actions={<div className="flex flex-wrap gap-2"><Button onClick={onOpenPortal} disabled={actionBusy !== null}>{actionBusy === 'portal' ? 'Opening Stripe…' : 'Manage in Stripe'}</Button><Link href="/profile?tab=sms" className="btn-secondary">Set up SMS alerts</Link></div>}>
          <h3 className="font-semibold text-[var(--ss-text)]">Six-month subscription active</h3>
          <p className="mt-1 text-sm text-[var(--ss-text-muted)]">{cancelAtPeriodEnd || terminalSubscription ? `Paid access ends ${accessDate || 'at the end of the current paid period'}.` : `Your next renewal is ${renewalDate || 'at the end of the current paid period'}.`}</p>
          <OperationalEvidence className="mt-3">Status: {subscriptionStatus || 'confirming payment'}</OperationalEvidence>
        </OperationalResult>
      ) : (
        <OperationalResult actions={<div className="flex flex-wrap gap-2"><Button onClick={onStartCheckout} disabled={actionBusy !== null || !canSubscribe}>{actionBusy === 'checkout' ? 'Opening Stripe…' : 'Subscribe for $5'}</Button>{hasBillingAccount ? <Button intent="secondary" onClick={onOpenPortal} disabled={actionBusy !== null}>{actionBusy === 'portal' ? 'Opening Stripe…' : 'Manage in Stripe'}</Button> : null}<Link href="/profile?tab=sms" className="btn-secondary">Set up SMS alerts</Link></div>}>
          <h3 className="font-semibold text-[var(--ss-text)]">Scarlet Sync subscription</h3>
          <p className="mt-1 text-sm text-[var(--ss-text-muted)]">$5 USD is charged every six months from signup. Cancel before the next renewal in Stripe; any paid access remains available through its paid end date.</p>
        </OperationalResult>
      )}
      <OperationalEvidence className="mt-3">
        <strong className="text-[var(--ss-text)]">SMS opening alerts</strong>
        <p className="mt-1">Each paid six-month period includes up to 50 SMS alerts, capped at 100 message segments. Longer messages may use multiple segments. Usage is not shown here. Alerts require a verified number, separate consent, enabled alerts, and current eligibility.</p>
      </OperationalEvidence>
      <OperationalEvidence className="mt-3">
        <strong className="text-[var(--ss-text)]">AI usage and allowance</strong>
        <p className="mt-1">Assistant usage is not metered by this plan. No current usage total is available here.</p>
      </OperationalEvidence>
    </OperationalSection>
  );
}

function ActiveProfilePanel(props: ProfileWorkspaceViewProps) {
  switch (props.activeTab) {
    case 'profile': return <ProfileAccountPanel {...props.account} />;
    case 'previous_classes': return <PreviousClassesPanel {...props.previousClasses} />;
    case 'connections': return <ConnectionsPanel {...props.connections} />;
    case 'schedules': return <SchedulesPanel {...props.schedules} />;
    case 'courses': return <CourseListsPanel {...props.courseLists} />;
    case 'degree_plans': return <DegreePlansPanel {...props.degreePlans} />;
    case 'billing': return <BillingPanel {...props.billing} />;
    case 'sms': return <SmsEnrollmentClient embedded />;
    case 'professional': return <ProfessionalPanel>{props.professionalContent}</ProfessionalPanel>;
    case 'memories': return <MemoriesPanel {...props.memories} />;
  }
}

export function ProfileWorkspaceView(props: ProfileWorkspaceViewProps) {
  if (props.embedded) {
    return (
      <OperationalPage className="min-h-0 max-w-none p-0">
        {props.summary.error ? <OperationalState kind={props.summary.state === 'error' ? 'error' : 'stale'} title={props.summary.state === 'error' ? 'Academic record could not be refreshed' : 'Some academic information may be stale'} description={props.summary.error} action={<Button intent="secondary" onClick={props.onRetry}>Retry</Button>} /> : null}
        <Surface id={`profile-panel-${props.activeTab}`} role="tabpanel" tabIndex={0}><ActiveProfilePanel {...props} /></Surface>
      </OperationalPage>
    );
  }
  return (
    <OperationalPage>
      <div className="mx-auto w-full max-w-7xl space-y-6">
        <h1 className="sr-only">Profile</h1>
        {props.summary.error ? <OperationalState kind={props.summary.state === 'error' ? 'error' : 'stale'} title={props.summary.state === 'error' ? 'Account workspace could not be refreshed' : 'Some account information may be stale'} description={props.summary.error} action={<Button intent="secondary" onClick={props.onRetry}>Retry</Button>} /> : null}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(240px,1fr)_minmax(0,3fr)]">
          <aside className="min-w-0 space-y-4"><ProfileTabNavigation {...props} /><Button intent="secondary" onClick={props.onSignOut}>Sign out</Button></aside>
          <Surface className="min-w-0" id={`profile-panel-${props.activeTab}`} role="tabpanel" aria-labelledby={`profile-tab-${props.activeTab}`} tabIndex={0}><ActiveProfilePanel {...props} /></Surface>
        </div>
      </div>
    </OperationalPage>
  );
}
